import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  BellIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
} from "@heroicons/react/24/solid";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/dark-logo-text-new.png";
import peakLogo from "../../assets/peakLogoIdea.png";
import { useAuth } from "../../contexts/AuthContext";
import { version } from "../../version";
import { useUserPoints } from "../../services/pointsService";
import { useUnreadNotificationCount } from "../../hooks/useUnreadNotificationCount";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCachedProfileImage } from "../../utils/imageCache";
import PushNotificationStatusIndicator from "./PushNotificationStatusIndicator";
import { organisationService } from "../../services/organisationService";

import { useSubscription } from "../../providers/SubscriptionProvider";
import ComingSoon from "../../components/ComingSoon";
import { isUnderAge } from "../../utils/dateUtils";
const UserProfileImage: React.FC = React.memo(() => {
  const { userData, currentUser } = useAuth();
  const profileImageUrl = userData?.imageUrlTiny || userData?.imageUrl;
  const { cachedUrl, loading } = useCachedProfileImage(profileImageUrl);

  // Memoize the profile image computation to prevent unnecessary recalculations
  const profileImageContent = useMemo(() => {
    // Show loading state while fetching cached image
    if (loading && profileImageUrl) {
      return (
        <div className="w-8 h-8 rounded-full bg-base-300 animate-pulse flex items-center justify-center">
          <span className="text-base-content/50 text-xs font-medium">...</span>
        </div>
      );
    }

    if (cachedUrl || profileImageUrl) {
      return (
        <img
          alt={`${userData?.name || "User"} profile`}
          src={cachedUrl || profileImageUrl}
          className="w-8 h-8 rounded-full object-cover"
          key={profileImageUrl} // Force re-render when imageUrl changes
        />
      );
    }

    return (
      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
        <span className="text-white text-sm font-medium">
          {userData?.name ? userData.name.charAt(0).toUpperCase() : "U"}
        </span>
      </div>
    );
  }, [cachedUrl, profileImageUrl, userData?.name, loading]);

  return profileImageContent;
});

const UserPointsDisplay: React.FC = React.memo(() => {
  const { points, loading } = useUserPoints();

  if (loading) {
    return (
      <span className="flex items-center justify-center rounded-md bg-base-300 text-base-content/80 text-sm font-semibold px-2 font-grotesk">
        -
      </span>
    );
  }

  return (
    <span className="flex items-center justify-center rounded-md bg-base-300 text-base-content/80 text-sm font-semibold border border-base-300 px-2 font-grotesk">
      {points}
      <span className="text-base-content/60 font-groteskLogo">^</span>
    </span>
  );
});

const NotificationBell: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const { unreadCount, loading, error } = useUnreadNotificationCount();

  const handleNotificationClick = React.useCallback(() => {
    navigate("/notifications");
  }, [navigate]);

  return (
    <button
      type="button"
      onClick={handleNotificationClick}
      className="relative rounded-full bg-base-100 p-1 text-base-content/50 hover:text-base-content/60 focus:outline-none"
    >
      <span className="absolute -inset-1.5" />
      <span className="sr-only">View notifications</span>
      <BellIcon aria-hidden="true" className="size-6" />
      {!loading && unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
});

const WizardButton: React.FC = React.memo(() => {
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const navigate = useNavigate();
  const { isPremium } = useSubscription();
  const [featureKeyForModal, setFeatureKeyForModal] = useState<string>("");
  const handleWizardClick = React.useCallback(
    (key: string, path: string) => {
      if (isPremium) {
        navigate(path);
      } else {
        setFeatureKeyForModal(key);
        setShowPremiumModal(true);
      }
    },
    [navigate, isPremium],
  );

  return (
    <>
      {/* Modal Popup */}
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-20 p-4">
          <div className="relative">
            <button
              onClick={() => setShowPremiumModal(false)}
              className="absolute -top-3 -right-3 bg-base-100 rounded-full px-2 py-1 text-sm"
            >
              ✕
            </button>
            <ComingSoon featureKey={featureKeyForModal} />
          </div>
        </div>
      )}

      {/* The Button */}
      <button
        type="button"
        onClick={() => handleWizardClick("wizardAi", "/chats")}
        className="relative rounded-full bg-base-100 p-1 text-base-content hover:text-base-content/60 focus:outline-none"
        title="AI Wizard"
      >
        <span className="absolute -inset-1.5" />
        <span className="sr-only">Open AI Wizard</span>
        <SparklesIcon aria-hidden="true" className="size-6" />
      </button>
    </>
  );
});

const SupportButton: React.FC = React.memo(() => {
  const navigate = useNavigate();

  const handleSupportClick = React.useCallback(() => {
    navigate("/support");
  }, [navigate]);

  return (
    <button
      type="button"
      onClick={handleSupportClick}
      className="relative rounded-full bg-base-100 p-1 text-base-content/50 hover:text-base-content/60 focus:outline-none"
    >
      <span className="absolute -inset-1.5" />
      <span className="sr-only">View notifications</span>
      <QuestionMarkCircleIcon aria-hidden="true" className="size-6" />
    </button>
  );
});

const LibraryDropdown: React.FC<{ isActive: (path: string) => boolean }> =
  React.memo(({ isActive }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    return (
      <div ref={ref} className="relative inline-flex items-center h-full">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-bold font-heading cursor-pointer h-full ${
            isActive("/sessions") || isActive("/activities")
              ? "border-base-content text-base-content"
              : "border-transparent text-base-content/60 hover:border-base-300 hover:text-base-content/80"
          }`}
        >
          Library
        </button>
        {open && (
          <div className="absolute left-0 top-full z-50 mt-2 w-48 rounded-md bg-base-100 shadow-lg ring-1 ring-black/5">
            <Link
              to="/sessions"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 rounded-t-md"
            >
              Sessions
            </Link>
            <Link
              to="/activities"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 rounded-b-md"
            >
              Exercises
            </Link>
          </div>
        )}
      </div>
    );
  });

const TrainingDiaryDropdown: React.FC<{
  isActive: (path: string) => boolean;
}> = React.memo(({ isActive }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex items-center h-full">
      <button
        type="button"
        id="diary-title"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-bold font-heading cursor-pointer h-full ${
          isActive("/feed") ||
          isActive("/sessions/completed") ||
          isActive("/performances")
            ? "border-base-content text-base-content"
            : "border-transparent text-base-content/60 hover:border-base-300 hover:text-base-content/80"
        }`}
      >
        Training Diary
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-48 rounded-md bg-base-100 shadow-lg ring-1 ring-black/5">
          <Link
            to="/feed"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 rounded-t-md"
          >
            Training Diary
          </Link>
          <Link
            to="/sessions/completed"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200"
          >
            Sessions Completed
          </Link>
          <Link
            to="/performances"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 rounded-b-md"
          >
            Performances
          </Link>
        </div>
      )}
    </div>
  );
});

const DataDropdown: React.FC<{ isActive: (path: string) => boolean }> =
  React.memo(({ isActive }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!open) return;
      const handler = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const dataActive =
      isActive("/insights/training") ||
      isActive("/insights/wellness") ||
      isActive("/insights/peak") ||
      isActive("/insights/sleep") ||
      isActive("/insights/activities");

    return (
      <div ref={ref} className="relative inline-flex items-center h-full">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-bold font-heading cursor-pointer h-full ${
            dataActive
              ? "border-base-content text-base-content"
              : "border-transparent text-base-content/60 hover:border-base-300 hover:text-base-content/80"
          }`}
        >
          Data
        </button>
        {open && (
          <div className="absolute left-0 top-full z-50 mt-2 w-52 rounded-md bg-base-100 shadow-lg ring-1 ring-black/5">
            <Link
              to="/insights/training"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 rounded-t-md"
            >
              Training Load
            </Link>
            <Link
              to="/insights/wellness"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200"
            >
              Wellness
            </Link>
            <Link
              to="/insights/peak"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200"
            >
              Peak Readiness
            </Link>
            <Link
              to="/insights/sleep"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200"
            >
              Health & Sleep
            </Link>
            <Link
              to="/insights/activities"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 rounded-b-md"
            >
              Activities & Exercises
            </Link>
          </div>
        )}
      </div>
    );
  });

interface HeaderProps {
  maxWidth?: string;
}

export default function Header({ maxWidth = "max-w-6xl" }: HeaderProps) {
  const { logout, currentUser, userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [coachingGroupIds, setCoachingGroupIds] = useState<string[]>([]);
  useEffect(() => {
    const fetchOrganisations = async () => {
      if (currentUser?.uid) {
        try {
          const ids = await organisationService.getCoachingGroupIds(
            currentUser.uid,
          );
          setCoachingGroupIds(ids);
        } catch (error) {
          console.error("Error fetching coaching group IDs:", error);
        }
      }
    };

    fetchOrganisations();
  }, [currentUser?.uid]);

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path: string) => location.pathname === path;

  // Social features are restricted to users aged 16 and above.
  const canAccessSocial = !isUnderAge(userData?.dob, 16);

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-base-100 shadow-sm w-full nav-safe-area">
      <div className={`mx-auto ${maxWidth} px-2 sm:px-6 lg:px-8`}>
        <div className="relative flex h-16 justify-between">
          <div className="flex flex-1 items-center pl-6 justify-left sm:items-stretch sm:justify-start">
            <div className="flex shrink-0 items-center">
              <Link to="/dashboard" className="flex flex-col items-start">
                <h1 className="text-3xl font-groteskLogo">
                  PEAK
                  <span className="font-bold text-primary -ml-1">^</span>
                </h1>
                <span className="text-xs text-base-content/60 -mt-2 opacity-60 font-normal">
                  v{version}
                </span>
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link
                to="/dashboard"
                className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-bold font-heading ${
                  isActive("/dashboard")
                    ? "border-base-content text-base-content"
                    : "border-transparent text-base-content/60 hover:border-base-300 hover:text-base-content/80"
                }`}
              >
                Dashboard
              </Link>

              <TrainingDiaryDropdown isActive={isActive} />
              <LibraryDropdown isActive={isActive} />
              <Link
                to="/schedule"
                className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-bold font-heading ${
                  isActive("/schedule")
                    ? "border-base-content text-base-content"
                    : "border-transparent text-base-content/60 hover:border-base-300 hover:text-base-content/80"
                }`}
              >
                Schedule
              </Link>
              <Link
                to="/programs"
                className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-bold font-heading ${
                  isActive("/programs")
                    ? "border-base-content text-base-content"
                    : "border-transparent text-base-content/60 hover:border-base-300 hover:text-base-content/80"
                }`}
              >
                Programs
              </Link>
              {canAccessSocial && (
                <Link
                  to="/connections"
                  className={`inline-flex items-center border-b-2 px-1 pt-1 text-sm font-bold font-heading ${
                    isActive("/connections")
                      ? "border-base-content text-base-content"
                      : "border-transparent text-base-content/60 hover:border-base-300 hover:text-base-content/80"
                  }`}
                >
                  Social
                </Link>
              )}

              <DataDropdown isActive={isActive} />
            </div>
          </div>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0 gap-x-2">
            <WizardButton />
            {/* <NotificationBell /> */}
            <PushNotificationStatusIndicator />

            {/* Profile dropdown */}
            <Menu as="div" className="relative ml-3">
              <div>
                <MenuButton className="relative flex rounded-full bg-base-100 text-sm focus:outline-none">
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">Open user menu</span>
                  <UserProfileImage />
                </MenuButton>
              </div>

              <MenuItems
                transition
                className="absolute right-0 z-45 mt-4 w-48 origin-top-right rounded-md bg-base-100 py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
              >
                <MenuItem>
                  <Link
                    to={`/profile/${currentUser?.uid || ""}`}
                    className="block px-4 py-2 text-sm text-base-content/80 data-focus:bg-base-200 data-focus:outline-none focus:outline-none"
                  >
                    Public Profile
                  </Link>
                </MenuItem>

                {coachingGroupIds && coachingGroupIds.length > 0 && (
                  <MenuItem>
                    <Link
                      to="/inbox"
                      className="block px-4 py-2 text-sm text-base-content/80 data-focus:bg-base-200 data-focus:outline-none focus:outline-none"
                    >
                      Inbox
                    </Link>
                  </MenuItem>
                )}
                <MenuItem>
                  <Link
                    to="/settings/eqlx"
                    className="flex items-center justify-between px-4 py-2 text-sm text-base-content/80 data-focus:bg-base-200 data-focus:outline-none focus:outline-none"
                  >
                    <span>STAXS Points</span>
                    <UserPointsDisplay />
                  </Link>
                </MenuItem>

                <MenuItem>
                  <Link
                    to="/support"
                    className="block px-4 py-2 text-sm text-base-content/80 data-focus:bg-base-200 data-focus:outline-none focus:outline-none"
                  >
                    Help & Support
                  </Link>
                </MenuItem>
                <MenuItem>
                  <Link
                    to="/settings"
                    className="block px-4 py-2 text-sm text-base-content/80 data-focus:bg-base-200 data-focus:outline-none focus:outline-none"
                  >
                    Settings
                  </Link>
                </MenuItem>
                <MenuItem>
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left px-4 py-2 text-sm text-base-content/80 data-focus:bg-base-200 data-focus:outline-none focus:outline-none"
                  >
                    Sign out
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>
    </nav>
  );
}
